import { useState, useEffect } from 'react';
import { useConfiguration } from '../../../../../../store/configurationStore';
import { UIInstrumentType } from '../../../types';
import { MathOperationsSectionProps } from './types';
import { validateFormulaSyntax } from '../../../utils/formulaValidation';

export const useMathOperationsSection = (props: MathOperationsSectionProps) => {
  const { templateId, instrumentType, sectionId, availableHeaders } = props;
  const { templates, updateMathOperation, getValidationForSection } =
    useConfiguration();

  // Get current math operation from store
  const template = templates.find(t => t.id === templateId);
  const instrumentConfig = template?.instrumentConfigs.find(
    ic => ic.instrumentType === (instrumentType as UIInstrumentType)
  );
  const section = instrumentConfig?.sections.find(s => s.id === sectionId);
  const mathOperation = section?.config.mathOperation || {
    formula: '',
    variables: {},
  };

  // Local state
  const [formula, setFormula] = useState<string>(mathOperation.formula);
  const [variables, setVariables] = useState<{ [key: string]: string }>(
    mathOperation.variables
  );
  const [selectedColumn, setSelectedColumn] = useState<string>('');

  // Update local state when store changes
  useEffect(() => {
    setFormula(mathOperation.formula);
    setVariables(mathOperation.variables);
  }, [mathOperation]);

  // Get next variable name (A, B, C, ...)
  const getNextVariableName = (): string => {
    const existingVars = Object.keys(variables);
    if (existingVars.length === 0) return 'A';

    const lastVar = existingVars.sort().pop();
    if (!lastVar) return 'A';

    const nextCharCode = lastVar.charCodeAt(0) + 1;
    return String.fromCharCode(nextCharCode);
  };

  // Add variable
  const addVariable = () => {
    if (!selectedColumn) return;

    const varName = getNextVariableName();
    const newVariables = { ...variables, [varName]: selectedColumn };

    setVariables(newVariables);
    setSelectedColumn('');
    // Keep existing formula unchanged — user types the formula manually
    updateMathOperation(
      templateId,
      instrumentType as UIInstrumentType,
      sectionId,
      { formula, variables: newVariables }
    );
  };

  // Remove variable
  const removeVariable = (varName: string) => {
    const newVariables = { ...variables };
    delete newVariables[varName];

    // Remove variable from formula
    const regex = new RegExp(`\\b${varName}\\b`, 'g');
    const newFormula = formula.replace(regex, '');

    setVariables(newVariables);
    setFormula(newFormula);

    // Save to store
    updateMathOperation(
      templateId,
      instrumentType as UIInstrumentType,
      sectionId,
      { formula: newFormula, variables: newVariables }
    );
  };

  // Update formula
  const updateFormula = (newFormula: string) => {
    setFormula(newFormula);
    // Always save to store — validation errors shown in UI via validationResult badge
    updateMathOperation(
      templateId,
      instrumentType as UIInstrumentType,
      sectionId,
      { formula: newFormula, variables }
    );
  };

  // Get formula preview with column names
  const getFormulaPreview = (): string => {
    if (!formula) return '⚠️ No formula entered';

    let preview = formula;
    Object.entries(variables).forEach(([varName, columnName]) => {
      preview = preview.replace(
        new RegExp(`\\b${varName}\\b`, 'g'),
        `[${columnName}]`
      );
    });
    return preview;
  };

  // Get available columns (exclude already used ones)
  const getAvailableColumns = (): string[] => {
    const usedColumns = Object.values(variables);
    return availableHeaders.filter(header => !usedColumns.includes(header));
  };

  const validation = validateFormulaSyntax(formula, variables);

  // Read validation from store
  const validationResult = getValidationForSection(sectionId);

  return {
    formula,
    variables,
    selectedColumn,
    nextVariableName: getNextVariableName(),
    availableColumns: getAvailableColumns(),
    validation,
    formulaPreview: getFormulaPreview(),
    setSelectedColumn,
    addVariable,
    removeVariable,
    updateFormula,
    validationResult,
  };
};
